import { fmt, getTrirMod, createRateResolver } from './lib/calc';
import { exportLeaveBehind } from './lib/leave-behind';

const root = document.querySelector('.roi-calculator');

let SECTOR_NAICS = {};
let STATES = [];
let rate = () => null;
let resolveLookup = () => null;

// Last computed value set, retained for the export.
let LAST_VALUES = null;

const byId = (id) => document.getElementById(id);

const numberFields = [
	'employees',
	'salary',
	'actual-mod',
	'b1',
	'b2',
	'b3',
	'b4',
	'b5',
	'flex',
	'current-spend',
];

function resolveLc() {
	return resolveLookup(
		byId('sector').value,
		byId('state').value,
		byId('naics').value
	);
}

// Tell the rep when the model used a different number than the one on screen.
// Notes go in .form-group because the theme wraps number inputs in .input-number.
function fieldNote(el, text) {
	const wrap = el.closest('.form-group') || el.parentElement;
	let note = wrap.querySelector('.clamp-note');
	if (text === null) {
		if (note) {
			note.remove();
		}
		delete el.dataset.clampedTo;
		return;
	}
	if (!note) {
		note = document.createElement('div');
		note.className = 'clamp-note';
		wrap.appendChild(note);
	}
	note.textContent = text;
}

// Records the value the model used so the blur handler can snap the field to it.
function setClamped(el, value, text) {
	fieldNote(el, text);
	el.dataset.clampedTo = value;
}

// Read a numeric input, clamped to a sane range. Returns null when empty.
// HTML min/max attributes are advisory only - they do not stop a typed value,
// so a clamp is disclosed at the field rather than applied silently.
function numIn(id, min, max) {
	const el = byId(id);
	if (!el) {
		return null;
	}
	fieldNote(el, null);
	const raw = el.value;
	if (raw === '' || raw === null) {
		return null;
	}
	const v = parseFloat(raw);
	if (isNaN(v) || !isFinite(v)) {
		return null;
	}
	const c = Math.min(Math.max(v, min), max);
	if (c !== v) {
		setClamped(
			el,
			c,
			'Outside the supported range. Calculated at ' + c + '.'
		);
	}
	return c;
}

// Headcounts and injury counts are whole numbers.
function intIn(id, min, max) {
	const v = numIn(id, min, max);
	if (v === null) {
		return null;
	}
	const r = Math.round(v);
	if (r !== v) {
		setClamped(byId(id), r, 'Whole numbers only. Calculated at ' + r + '.');
	}
	return r;
}

// Returns the typed ex-mod, or null when the field is blank or unusable.
// 0 and negatives mean "not supplied", not "clamp me to the 0.50 floor".
// getMod() and calc() must agree on this or Y1 can cost more than today.
function actualMod() {
	const el = byId('actual-mod');
	if (!el) {
		return null;
	}
	fieldNote(el, null);
	const raw = el.value;
	if (raw === '' || raw === null) {
		return null;
	}
	const v = parseFloat(raw);
	if (isNaN(v) || !isFinite(v)) {
		return null;
	}
	if (v <= 0) {
		fieldNote(
			el,
			'Zero or negative is not a valid ex-mod. Using the TRIR-derived mod.'
		);
		return null;
	}
	const c = Math.min(Math.max(v, 0.5), 3.0);
	if (c !== v) {
		setClamped(
			el,
			c,
			'Outside the supported range. Calculated at ' + c + '.'
		);
	}
	return c;
}

function getMod() {
	const am = actualMod();
	if (am !== null) {
		return am;
	}
	return getTrirMod(parseFloat(byId('trir').value));
}

function updateMod() {
	byId('mod-disp').textContent = getMod().toFixed(2);
}

function addOption(parent, value, label) {
	const o = document.createElement('option');
	o.value = value;
	o.textContent = label;
	parent.appendChild(o);
}

function resetLc() {
	// The export reads LAST_VALUES. Any selection reset must invalidate it.
	LAST_VALUES = null;
	byId('lc-num').textContent = 'Select sector, state and industry above';
	byId('lc-num').className = 'lc-na';
	byId('lc-sub').textContent = '';
}

function onSector() {
	const sector = byId('sector').value;
	const stSel = byId('state');
	const naSel = byId('naics');

	stSel.innerHTML = '<option value="">— Select state —</option>';
	naSel.innerHTML = '<option value="">— Select industry —</option>';
	stSel.disabled = !sector;
	naSel.disabled = true;

	resetLc();
	byId('output').classList.add('hidden');
	if (!sector) {
		return;
	}

	// General is already in STATES from the data; list it first.
	[...STATES]
		.sort((a, b) => {
			if (a === 'General') {
				return -1;
			}
			if (b === 'General') {
				return 1;
			}
			return a.localeCompare(b);
		})
		.forEach((s) => addOption(stSel, s, s));
}

function onState() {
	const sector = byId('sector').value;
	const state = byId('state').value;
	const naSel = byId('naics');

	naSel.innerHTML = '<option value="">— Select industry —</option>';
	naSel.disabled = !state;

	resetLc();
	byId('output').classList.add('hidden');
	if (!state) {
		return;
	}

	// NAICS for this sector, split into exact-rate and proxy-rate groups so a rep
	// can see at a glance which selections carry a real state rate.
	const list = SECTOR_NAICS[sector] || [];
	const exact = list.filter((n) => rate(sector, state, n) !== null);
	const proxy = list.filter((n) => rate(sector, state, n) === null);

	const addTo = (parent, n, mark) =>
		addOption(parent, n, n + (mark ? '  (proxy rate)' : ''));

	if (exact.length && proxy.length) {
		const g1 = document.createElement('optgroup');
		g1.label = state + ' rate on file';
		exact.forEach((n) => addTo(g1, n, false));
		naSel.appendChild(g1);

		const g2 = document.createElement('optgroup');
		g2.label = 'No ' + state + ' rate - proxy applied';
		proxy.forEach((n) => addTo(g2, n, true));
		naSel.appendChild(g2);
	} else {
		list.forEach((n) => addTo(naSel, n, proxy.length > 0));
	}
}

function updateSummary(v) {
	LAST_VALUES = v;

	const rows = [
		['[Industry Sector]', v.sector, 'INDUSTRY field', false],
		['[Sub-Industry]', v.naics, 'SUB-INDUSTRY field', false],
		['[State]', v.state, 'STATE field', false],
		[
			'[Loss Cost Basis]',
			v.lcBasis + (v.lcExact ? '' : ' (proxy)'),
			'Footnote - disclose on leave-behind',
			false,
		],
		['[$ Payroll]', fmt(v.payroll), 'ANNUAL PAYROLL', false],
		['[TRIR]', v.trirLabel, 'TRIR field', false],
		['[Employees]', v.employeesLabel, 'EMPLOYEES', false],
		["[Workers' Comp Premium]", fmt(v.wcCurrent), 'Card 1', true],
		['[Injury Costs]', fmt(v.injTotal), 'Card 2', true],
		['[Total Exposure]', fmt(v.totalExp), 'Card 3 — RED', true],
		['[Flex Investment]', fmt(v.flex), 'ROI card 1', false],
		[
			'[Displaced Spend]',
			fmt(v.displaced),
			'NEW - add placeholder to deck',
			false,
		],
		['[Annual Savings]', fmt(v.annSavings), 'ROI card 2', true],
		['[Y1 Net Value]', fmt(v.y1Net), 'ROI card 3', true],
		['[Y2 Net Value]', fmt(v.yrTwoNet), 'ROI card 4', true],
	];

	const tbody = byId('ps-body');
	tbody.innerHTML = '';
	rows.forEach(([ph, val, where, hi]) => {
		const tr = document.createElement('tr');
		[
			['ph' + (hi ? ' hi' : ''), ph],
			['val' + (hi ? ' hi' : ''), val],
			['note-col', where],
		].forEach(([className, text]) => {
			const td = document.createElement('td');
			td.className = className;
			td.textContent = text;
			tr.appendChild(td);
		});
		tbody.appendChild(tr);
	});
}

function calc() {
	updateMod();

	const lcRes = resolveLc();
	const lc = lcRes ? lcRes.rate : null;
	const emp = intIn('employees', 0, 1000000) || 0;
	const sal = numIn('salary', 0, 10000000) || 0;
	const payroll = emp * sal;

	byId('payroll-disp').textContent = fmt(payroll);

	if (!lc || !payroll) {
		byId('output').classList.add('hidden');
		LAST_VALUES = null;
		return;
	}

	const mod = getMod();
	const trirVal = parseFloat(byId('trir').value);
	const hasAMod = actualMod() !== null;

	const wcCurrent = payroll * (lc / 100) * mod;

	// Y1: TRIR reduced 20%
	const trirY1 = trirVal * 0.8;
	const modY1raw = getTrirMod(trirY1);
	const modCurr = getTrirMod(trirVal);
	const modY1 = hasAMod ? mod * (modY1raw / (modCurr || 1)) : modY1raw;

	const wcY1 = payroll * (lc / 100) * modY1;
	const wcSavings = wcCurrent - wcY1;

	const b = ['b1', 'b2', 'b3', 'b4', 'b5'].map(
		(id) => intIn(id, 0, 100000) || 0
	);
	const costs = [20000, 32000, 50000, 100000, 160000];
	const injDirect = b.reduce((s, v, i) => s + v * costs[i], 0);
	const injTotal = injDirect * 2.1;
	const injY1Dir = b.reduce((s, v, i) => s + v * 0.8 * costs[i], 0);
	const injY1Total = injY1Dir * 2.1;
	const injSavings = injTotal - injY1Total;

	const flex = numIn('flex', 0, 100000000) || 0;
	// Spend the prospect already makes that Flex displaces. This is a cost
	// substitution, not a safety saving: it is credited at full value every year
	// and is deliberately NOT multiplied by the Y2 savings-growth factor.
	const displaced = numIn('current-spend', 0, 100000000) || 0;
	const annSavings = wcSavings + injSavings;
	const totalExp = wcCurrent + injTotal;
	const y1Net = annSavings + displaced - flex;
	const y2Savings = annSavings * 1.3;
	const y2Platform = flex * 0.8;
	const yrTwoNet = y2Savings + displaced - y2Platform;

	byId('o-wc').textContent = fmt(wcCurrent);
	byId('o-inj').textContent = fmt(injTotal);
	byId('o-total').textContent = fmt(totalExp);
	byId('o-flex').textContent = fmt(flex);
	byId('o-sav').textContent = fmt(annSavings);
	byId('o-y1net').textContent = fmt(y1Net);
	byId('o-2yr').textContent = fmt(yrTwoNet);

	const offRow = byId('o-offset-row');
	const recon = byId('o-recon');
	if (displaced > 0) {
		byId('o-disp').textContent = fmt(displaced);
		offRow.classList.remove('hidden');
		recon.classList.remove('hidden');
		recon.textContent =
			'Y1 Net Value = safety savings ' +
			fmt(annSavings) +
			' + displaced spend ' +
			fmt(displaced) +
			' - Flex ' +
			fmt(flex) +
			' = ' +
			fmt(y1Net) +
			'.' +
			' Y2 applies the 1.30x growth factor to safety savings only.';
	} else {
		offRow.classList.add('hidden');
		recon.classList.add('hidden');
	}

	byId('output').classList.remove('hidden');
	byId('summary-card').style.display = '';

	updateSummary({
		lc,
		lcBasis: lcRes.basis,
		lcExact: lcRes.exact,
		displaced,
		// Snapshot of the selections these figures were computed from. The export
		// reads these, never the live DOM, so the two can never disagree.
		sector: byId('sector').value,
		state: byId('state').value,
		naics: byId('naics').value,
		trirLabel: byId('trir').value,
		employeesLabel: byId('employees').value,
		payroll,
		mod,
		modY1,
		trirVal,
		trirY1,
		wcCurrent,
		wcY1,
		wcSavings,
		injTotal,
		injSavings,
		annSavings,
		totalExp,
		flex,
		y1Net,
		yrTwoNet,
	});
}

function onNaics() {
	const r = resolveLc();
	const numEl = byId('lc-num');
	const subEl = byId('lc-sub');

	if (r) {
		numEl.textContent = r.rate.toFixed(3);
		numEl.className = 'lc-num';
		subEl.textContent =
			'per $100 payroll - ' + r.basis + (r.exact ? '' : '. ' + r.note);
		subEl.style.color = r.exact ? '' : '#B26B00';
	} else {
		numEl.textContent = 'No rate found for this combination';
		numEl.className = 'lc-na';
		subEl.textContent = '';
		subEl.style.color = '';
	}
	calc();
}

function finalize() {
	const card = byId('summary-card');
	card.scrollIntoView({ behavior: 'smooth' });
	card.style.border = '2px solid #CC2200';
	setTimeout(() => {
		card.style.border = '1px solid #D8D4EC';
	}, 2000);
}

function init(data) {
	SECTOR_NAICS = data.SECTOR_NAICS || {};
	STATES = data.STATES || [];
	({ rate, resolveLc: resolveLookup } = createRateResolver(
		data.LOOKUP || {}
	));

	const sectorSel = byId('sector');
	Object.keys(SECTOR_NAICS)
		.sort()
		.forEach((s) => addOption(sectorSel, s, s));

	updateMod();

	sectorSel.addEventListener('change', onSector);
	byId('state').addEventListener('change', onState);
	byId('naics').addEventListener('change', onNaics);
	byId('trir').addEventListener('change', calc);

	numberFields.forEach((id) => {
		const el = byId(id);
		if (el) {
			el.addEventListener('input', calc);
		}
	});

	// The theme's global number-input stepper buttons (js/__frontend/forms.js)
	// change the input's value via stepUp()/stepDown(), which never fires an
	// input/change event. Recalculate directly on click instead.
	const inputsWrap = root.querySelector('.inputs-wrap');
	if (inputsWrap) {
		inputsWrap.addEventListener('click', (e) => {
			if (e.target.closest('.input-number__button')) {
				calc();
			}
		});
	}

	// When a typed value was clamped or rounded, snap the field to the number the
	// model actually used once the rep leaves it, so screen and export agree.
	numberFields.forEach((id) => {
		const el = byId(id);
		if (el) {
			el.addEventListener('blur', () => {
				if (el.dataset.clampedTo) {
					el.value = el.dataset.clampedTo;
					calc();
				}
			});
		}
	});

	const submitBtn = root.querySelector('.js-roi-finalize');
	if (submitBtn) {
		submitBtn.addEventListener('click', finalize);
	}

	const exportBtn = root.querySelector('.js-roi-export');
	if (exportBtn) {
		exportBtn.addEventListener('click', () =>
			exportLeaveBehind(LAST_VALUES)
		);
	}
}

if (root && root.dataset.lookupUrl) {
	fetch(root.dataset.lookupUrl)
		.then((response) => {
			if (!response.ok) {
				throw new Error(response.status);
			}
			return response.json();
		})
		.then(init, () => {
			const numEl = byId('lc-num');
			if (numEl) {
				numEl.textContent =
					'Rate data could not be loaded. Refresh the page to try again.';
			}
		});
}
